import { ExportDialog } from '@aibees/elephant/components/export-dialog';
import { useIntegrationPrepareDataBasedOnCount } from '@aibees/elephant/components/integration-tabs/use-integration-prepare-data-based-on-count';
import { integrationConfig } from '@aibees/elephant/constants/integration';
import { useTableActionContext } from '@aibees/elephant/context';
import { ROUTES } from '@aibees/elephant/router';
import {
  IntegrationConfig,
  IntegrationStatus,
} from '@aibees/elephant/types/integration';
import { Chip, Icon } from '@aibees/shared/components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useCheckUpgradeForIntegration } from '@aibees/elephant/hooks/integration/use-check-upgrade-for-integration';
import { useIsSmartleadIntegrationEnabled } from '@aibees/elephant/hooks/integration/use-is-smartlead-integration-enabled';
import { useMatchRoute } from '@aibees/shared/hooks';
import { useRouter } from '@aibees/shared/router';
import { useIsInternalUser } from '@aibees/shared/workspace/hooks/use-is-internal-user';
import { Avatar, Box, Button, Menu } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { StyledActionMenuItem } from '../search-table-header/styles';
import * as SX from './sx';
import type { SearchTableActionDialogExportProps as Props } from './types';

const NESTED_MENU_ANCHOR_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};
const NESTED_MENU_TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'left' as const,
};

export const SearchTableActionDialogExport = memo(
  ({ className, table, disabled, isSmall }: Props) => {
    const { exportDialogState, setIntegrationConfig } = useTableActionContext();
    const isSmartleadIntegrationEnabled = useIsSmartleadIntegrationEnabled();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [nestedAnchorEl, setNestedAnchorEl] = useState<null | HTMLElement>(
      null
    );
    const checkUpgradeForIntegration = useCheckUpgradeForIntegration();
    const open = Boolean(anchorEl);
    const nestedOpen = Boolean(nestedAnchorEl);
    const isPeopleRoute = useMatchRoute(ROUTES.people);
    const isSearchPeopleRoute = useMatchRoute(ROUTES.searchPeople);
    // TODO handle ut after getting data from backend
    const hasIntegrations = isPeopleRoute || isSearchPeopleRoute;
    const internalUser = useIsInternalUser();
    const items = useIntegrationPrepareDataBasedOnCount(integrationConfig);
    const nestedItems = items.filter(
      (x) => x.status !== IntegrationStatus.ComingSoon
    );
    const { redirect } = useRouter();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setIntegrationConfig(undefined);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    const handleNestedMenuOpen = (event: React.MouseEvent<HTMLLIElement>) => {
      setNestedAnchorEl(event.currentTarget);
    };

    const handleNestedMenuClose = () => {
      setNestedAnchorEl(null);
    };

    const handleOpenCsvExportDialog = useCallback(() => {
      exportDialogState.onTrue();
      setAnchorEl(null);
    }, [exportDialogState]);

    const handleClickIntegrationsItem = (item: IntegrationConfig) => {
      if (checkUpgradeForIntegration && !internalUser) {
        redirect(ROUTES.priceAndPlan);
      } else {
        exportDialogState.onTrue();
        setNestedAnchorEl(null);
        setAnchorEl(null);
        setIntegrationConfig(item);
      }
    };

    const endIcon = useMemo(() => {
      if (isSmall) return null;
      return <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} />;
    }, [isSmall, open]);

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Icon name="export" size={16} />}
          endIcon={endIcon}
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          disabled={disabled}
          onClick={handleClick}
        >
          {!isSmall && 'Export'}
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <StyledActionMenuItem onClick={handleOpenCsvExportDialog}>
            CSV Files
          </StyledActionMenuItem>
          {hasIntegrations && isSmartleadIntegrationEnabled && (
            <Box component="span" sx={{ display: 'contents' }}>
              <StyledActionMenuItem
                sx={SX.integrationsMenuWrapper}
                onClick={handleNestedMenuOpen}
              >
                Integrations
                <Icon name="chevron-right" />
              </StyledActionMenuItem>

              <Menu
                id="nested-menu"
                anchorEl={nestedAnchorEl}
                open={nestedOpen}
                onClose={handleNestedMenuClose}
                anchorOrigin={NESTED_MENU_ANCHOR_ORIGIN}
                transformOrigin={NESTED_MENU_TRANSFORM_ORIGIN}
              >
                {nestedItems.map((item) => (
                  <StyledActionMenuItem
                    key={item.name}
                    sx={{ gap: 1 }}
                    onClick={() => handleClickIntegrationsItem(item)}
                  >
                    <Avatar src={item.icon} size={24} variant="rounded" />

                    {item.name}

                    {checkUpgradeForIntegration && !internalUser && (
                      <Chip
                        label="Upgrade"
                        color="warning"
                        size="xsmall"
                        shape="rounded"
                      />
                    )}
                  </StyledActionMenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Menu>

        <ExportDialog />
      </>
    );
  }
);

SearchTableActionDialogExport.displayName = 'SearchTableActionDialogExport';
